import { useEffect, useState } from "react";

import { deleteEvent, fetchEvents, saveEvent, updateEvent, updateEventEnabled } from "../services/advisorService";

const EMPTY_EVENT = {
  eventCode: "",
  eventName: "",
  region: "INDIA",
  category: "MACRO",
  keywords: "",
  enabled: true,
  impactBias: "NSE_EVENT_RISK",
  impactWeight: 5,
  rssQuery: "",
  notes: "",
};

const REGION_OPTIONS = ["INDIA", "GLOBAL"];
const CATEGORY_OPTIONS = ["MACRO", "CENTRAL_BANK", "GEOPOLITICS", "POLITICS", "COMMODITY", "EARNINGS", "GLOBAL_MARKET"];
const IMPACT_BIAS_OPTIONS = ["NSE_EVENT_RISK", "NSE_MACRO", "RISK_NEGATIVE", "RATE_SENSITIVE", "GLOBAL_RISK_ON", "GLOBAL_RISK_OFF"];
const IMPACT_WEIGHT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function eventKey(event, index) {
  return event.eventId ?? event.eventCode ?? index;
}

function sameEvent(left, right) {
  const leftId = left?.eventId ?? left?.id;
  const rightId = right?.eventId ?? right?.id;
  if (leftId && rightId) return leftId === rightId;

  const leftCode = left?.eventCode ?? left?.code;
  const rightCode = right?.eventCode ?? right?.code;
  return Boolean(leftCode && rightCode && leftCode === rightCode);
}

function sortEvents(events) {
  return [...events].sort((a, b) => {
    if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
    return String(a.region ?? "").localeCompare(String(b.region ?? "")) ||
      String(a.eventName ?? "").localeCompare(String(b.eventName ?? ""));
  });
}

function prepareEventDraft(event) {
  return {
    ...EMPTY_EVENT,
    ...event,
    eventCode: event?.eventCode ?? event?.code ?? "",
    eventName: event?.eventName ?? event?.name ?? "",
    impactWeight: event?.impactWeight ?? 5,
    keywords: event?.keywords ?? "",
    rssQuery: event?.rssQuery ?? "",
    notes: event?.notes ?? "",
    enabled: event?.enabled ?? true,
  };
}

function normalizeEventPayload(event) {
  return {
    ...event,
    eventCode: String(event.eventCode ?? "").trim().toUpperCase().replace(/\s+/g, "_"),
    eventName: String(event.eventName ?? "").trim(),
    keywords: String(event.keywords ?? "").trim(),
    rssQuery: String(event.rssQuery ?? "").trim(),
    notes: String(event.notes ?? "").trim(),
    impactWeight: Number(event.impactWeight) || 5,
    enabled: Boolean(event.enabled),
  };
}

export default function EventConfigPage() {
  const [events, setEvents] = useState([]);
  const [draft, setDraft] = useState(EMPTY_EVENT);
  const [editingKey, setEditingKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadEvents = async () => {
    setLoading(true);
    setError("");

    try {
      setEvents(sortEvents(await fetchEvents()));
    } catch (err) {
      console.error("Unable to load events", err);
      setError("Unable to load event configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const resetDraft = () => {
    setDraft(EMPTY_EVENT);
    setEditingKey("");
  };

  const handleEdit = (event, index) => {
    setDraft(prepareEventDraft(event));
    setEditingKey(String(eventKey(event, index)));
    setError("");
    setMessage("");
  };

  const handleToggle = async (event, enabled) => {
    const key = eventKey(event, events.indexOf(event));
    setSavingKey(`toggle-${key}`);
    setError("");
    setEvents((current) =>
      current.map((item) => (sameEvent(item, event) ? { ...item, enabled } : item))
    );

    try {
      await updateEventEnabled(event, enabled);
      await loadEvents();
      setMessage(enabled ? "Event enabled." : "Event disabled.");
    } catch (err) {
      console.error("Unable to update event", err);
      setError("Unable to update event selection.");
      await loadEvents();
    } finally {
      setSavingKey("");
    }
  };

  const handleSave = async (submitEvent) => {
    submitEvent.preventDefault();
    const isEditing = Boolean(editingKey);
    setSavingKey(isEditing ? `edit-${editingKey}` : "add");
    setError("");
    setMessage("");

    try {
      const payload = normalizeEventPayload(draft);
      if (isEditing) {
        await updateEvent(payload);
      } else {
        await saveEvent(payload);
      }
      resetDraft();
      await loadEvents();
      setMessage(isEditing ? "Event updated." : "Event added.");
    } catch (err) {
      console.error("Unable to save event", err);
      setError(isEditing ? "Unable to update event." : "Unable to add event.");
    } finally {
      setSavingKey("");
    }
  };

  const handleDelete = async (event) => {
    const key = eventKey(event, events.indexOf(event));
    setSavingKey(`delete-${key}`);
    setError("");
    setMessage("");

    try {
      await deleteEvent(event);
      setEvents((current) => current.filter((item) => !sameEvent(item, event)));
      setMessage("Event deleted.");
    } catch (err) {
      console.error("Unable to delete event", err);
      setError("Unable to delete event. Confirm the backend delete route is enabled.");
      await loadEvents();
    } finally {
      setSavingKey("");
    }
  };

  return (
    <section className="event-config-page">
      <div className="section-heading">
        <div>
          <h2>Profile Config</h2>
          <p>Configure the event intelligence inputs used by the market bias batch.</p>
        </div>
        <button className="secondary-action-btn" type="button" onClick={loadEvents} disabled={loading}>
          Reload
        </button>
      </div>

      {error && <div className="desk-error">{error}</div>}
      {message && <div className="desk-success">{message}</div>}

      <div className="event-score-guide" aria-label="Event score guide">
        <div>
          <span>Global Info</span>
          <strong>Event Score Range</strong>
        </div>
        <p>
          Event scores normally run from 0 to 10, with 5 as neutral. When the score moves down from 5 toward 0, the event is turning positive and may be ready to close as a positive event. Closed events do not need a negative score.
        </p>
        <p>
          When the score moves up from 5 toward 10, the event is becoming negative. A score of 10 means the event is at its worst normal stage but still on controllable ground.
        </p>
        <p>
          If the score crosses above 10, treat it as an extreme market-impact event. That level can shake the market and, in severe cases, create collapse-level risk.
        </p>
      </div>

      <form className="event-form" onSubmit={handleSave}>
        <input
          aria-label="Event code"
          placeholder="EVENT_CODE"
          value={draft.eventCode}
          onChange={(event) => updateDraft("eventCode", event.target.value)}
          required
        />
        <input
          aria-label="Event name"
          placeholder="Event name"
          value={draft.eventName}
          onChange={(event) => updateDraft("eventName", event.target.value)}
          required
        />
        <select value={draft.region} onChange={(event) => updateDraft("region", event.target.value)} aria-label="Region">
          {REGION_OPTIONS.map((region) => <option value={region} key={region}>{region}</option>)}
        </select>
        <select value={draft.category} onChange={(event) => updateDraft("category", event.target.value)} aria-label="Category">
          {CATEGORY_OPTIONS.map((category) => <option value={category} key={category}>{category}</option>)}
        </select>
        <select value={draft.impactBias} onChange={(event) => updateDraft("impactBias", event.target.value)} aria-label="Impact bias">
          {IMPACT_BIAS_OPTIONS.map((impactBias) => <option value={impactBias} key={impactBias}>{impactBias}</option>)}
        </select>
        <select value={draft.impactWeight} onChange={(event) => updateDraft("impactWeight", event.target.value)} aria-label="Impact weight">
          {IMPACT_WEIGHT_OPTIONS.map((weight) => <option value={weight} key={weight}>Weight {weight}</option>)}
        </select>
        <input
          aria-label="Keywords"
          className="event-wide-input"
          placeholder="Keywords, comma separated"
          value={draft.keywords}
          onChange={(event) => updateDraft("keywords", event.target.value)}
          required
        />
        <input
          aria-label="RSS query"
          className="event-wide-input"
          placeholder="RSS query"
          value={draft.rssQuery}
          onChange={(event) => updateDraft("rssQuery", event.target.value)}
        />
        <input
          aria-label="Notes"
          className="event-wide-input"
          placeholder="Notes"
          value={draft.notes}
          onChange={(event) => updateDraft("notes", event.target.value)}
        />
        <button className="primary-action-btn" type="submit" disabled={savingKey === "add" || savingKey === `edit-${editingKey}`}>
          {savingKey === "add" ? "Adding" : savingKey === `edit-${editingKey}` ? "Saving" : editingKey ? "Save Event" : "Add Event"}
        </button>
        {editingKey && (
          <button className="secondary-action-btn" type="button" onClick={resetDraft} disabled={Boolean(savingKey)}>
            Cancel
          </button>
        )}
      </form>

      <div className="event-table-wrap">
        <table className="event-table">
          <thead>
            <tr>
              <th>Active</th>
              <th>Event</th>
              <th>Region</th>
              <th>Category</th>
              <th>Impact</th>
              <th>Keywords / RSS</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7">Loading events...</td></tr>
            ) : events.length ? (
              events.map((event, index) => {
                const key = eventKey(event, index);
                const deleting = savingKey === `delete-${key}`;
                const editing = editingKey === String(key);
                const toggling = savingKey === `toggle-${key}`;
                return (
                  <tr className={editing ? "event-row-editing" : ""} key={key}>
                    <td>
                      <label className="event-check">
                        <input
                          type="checkbox"
                          checked={Boolean(event.enabled)}
                          disabled={toggling || deleting}
                          onChange={(changeEvent) => handleToggle(event, changeEvent.target.checked)}
                        />
                      </label>
                    </td>
                    <td>
                      <strong>{event.eventName}</strong>
                      <span>{event.eventCode}</span>
                      {event.notes && <p>{event.notes}</p>}
                    </td>
                    <td>{event.region}</td>
                    <td>{event.category}</td>
                    <td>{event.impactBias} / {event.impactWeight}</td>
                    <td>
                      <span>{event.keywords}</span>
                      {event.rssQuery && <p>RSS: {event.rssQuery}</p>}
                    </td>
                    <td>
                      <div className="event-action-row">
                        <button
                          className="secondary-action-btn"
                          type="button"
                          disabled={deleting || toggling || Boolean(savingKey)}
                          onClick={() => handleEdit(event, index)}
                        >
                          {editing ? "Editing" : "Edit"}
                        </button>
                        <button
                          className="danger-action-btn"
                          type="button"
                          disabled={deleting || toggling || editing}
                          onClick={() => handleDelete(event)}
                        >
                          {deleting ? "Deleting" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan="7">No events configured.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
